import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTheme } from '@/store/useTheme'
import type { Palette } from '@/store/palettes'
import { returnAfterPractice } from '@/utils/practiceReturn'
import styles from './GroundingPage.module.css'

// ── Options ───────────────────────────────────────────────────────────────

type OptionId = 'feet' | 'scan' | 'temperature' | 'sound' | 'pause'

const OPTIONS: { id: OptionId; label: string; subtitle: string }[] = [
  {
    id: 'feet',
    label: 'Feet on the floor',
    subtitle: 'Feel the weight of your feet. Notice the ground beneath you.',
  },
  {
    id: 'scan',
    label: 'Body scan',
    subtitle: 'Move slowly from head to toe, just noticing.',
  },
  {
    id: 'temperature',
    label: 'Temperature',
    subtitle: 'Notice the temperature of the air on your skin and your breath.',
  },
  {
    id: 'sound',
    label: 'Sound',
    subtitle: 'Close your eyes and just listen. Near sounds, far sounds, the space between.',
  },
  {
    id: 'pause',
    label: 'The pause',
    subtitle: 'Stop. Be still for 60 seconds. No instructions.',
  },
]

// ── Shared sub-components ─────────────────────────────────────────────────

function PulseCircle({ ring, bg }: { ring: string; bg: string }) {
  return (
    <div className={styles.pulseWrap}>
      <div
        className={styles.pulseRing}
        style={{ borderColor: `color-mix(in srgb, ${ring} 38%, transparent)` }}
      />
      <div
        className={styles.pulseCircle}
        style={{
          background:  `color-mix(in srgb, ${ring} 28%, ${bg})`,
          borderColor: `color-mix(in srgb, ${ring} 55%, transparent)`,
        }}
      />
    </div>
  )
}

const RING_R    = 72
const RING_CIRC = 2 * Math.PI * RING_R

function CountdownRing({
  elapsed,
  total,
  ring,
}: {
  elapsed: number
  total:   number
  ring:    string
}) {
  const progress  = Math.min(elapsed / total, 1)
  const remaining = Math.ceil(Math.max(0, total - elapsed))
  const offset    = RING_CIRC * (1 - progress)

  return (
    <div className={styles.countdownRingWrap}>
      <svg width={176} height={176} viewBox="0 0 176 176" aria-hidden="true">
        <circle
          cx={88} cy={88} r={RING_R}
          fill="none"
          stroke={`color-mix(in srgb, ${ring} 15%, transparent)`}
          strokeWidth={3}
        />
        <circle
          cx={88} cy={88} r={RING_R}
          fill="none"
          stroke={`color-mix(in srgb, ${ring} 65%, transparent)`}
          strokeWidth={3}
          strokeDasharray={RING_CIRC}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transform:       'rotate(-90deg)',
            transformOrigin: '88px 88px',
            transition:      'stroke-dashoffset 1s linear',
          }}
        />
      </svg>
      <div
        className={styles.countdownNumber}
        style={{ color: ring }}
        aria-live="polite"
        aria-label={`${remaining} seconds remaining`}
      >
        {remaining}
      </div>
    </div>
  )
}

function DoneBtn({
  onClick,
  ring,
  text,
}: {
  onClick: () => void
  ring:    string
  text:    string
}) {
  return (
    <button
      className={styles.doneBtn}
      style={{ borderColor: `color-mix(in srgb, ${ring} 45%, transparent)`, color: text }}
      onClick={onClick}
    >
      Done
    </button>
  )
}

// ── Feet guide ────────────────────────────────────────────────────────────

const FEET_PHRASES = [
  'The weight of your feet.',
  'The contact with the floor.',
  'Just this.',
]

function FeetGuide({ onDone, palette }: { onDone: () => void; palette: Palette }) {
  const [phraseIdx, setPhraseIdx] = useState(0)

  useEffect(() => {
    if (phraseIdx >= FEET_PHRASES.length - 1) return
    const t = setTimeout(() => setPhraseIdx(i => i + 1), 4500)
    return () => clearTimeout(t)
  }, [phraseIdx])

  return (
    <div className={styles.guideView}>
      <PulseCircle ring={palette.ring} bg={palette.bg} />
      <div className={styles.phrases}>
        {FEET_PHRASES.map((p, i) => (
          <p
            key={i}
            className={styles.phrase}
            style={{
              opacity:   i > phraseIdx ? 0 : i === phraseIdx ? 1 : 0.4,
              transform: i > phraseIdx ? 'translateY(8px)' : 'none',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
              color: palette.text,
            }}
          >
            {p}
          </p>
        ))}
      </div>
      <DoneBtn onClick={onDone} ring={palette.ring} text={palette.text} />
    </div>
  )
}

// ── Body scan guide ───────────────────────────────────────────────────────

const SCAN_STEPS = [
  'Head and face',
  'Neck and shoulders',
  'Chest and back',
  'Arms and hands',
  'Belly',
  'Hips and legs',
  'Feet and floor',
]
const SCAN_STEP_MS = 7000

function ScanGuide({ onDone, palette }: { onDone: () => void; palette: Palette }) {
  const [step,    setStep]    = useState(0)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (step >= SCAN_STEPS.length - 1) return
    let inner: ReturnType<typeof setTimeout>
    const outer = setTimeout(() => {
      setVisible(false)
      inner = setTimeout(() => {
        setStep(s => s + 1)
        setVisible(true)
      }, 420)
    }, SCAN_STEP_MS)
    return () => { clearTimeout(outer); clearTimeout(inner) }
  }, [step])

  return (
    <div className={styles.guideView}>
      <p className={styles.scanSub} style={{ color: palette.text }}>
        Just notice. No need to change anything.
      </p>
      <p
        className={styles.scanStep}
        style={{
          color:   palette.ring,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.42s ease',
        }}
        aria-live="polite"
      >
        {SCAN_STEPS[step]}
      </p>
      <div className={styles.scanDots} role="presentation">
        {SCAN_STEPS.map((_, i) => (
          <div
            key={i}
            className={styles.scanDot}
            style={{
              background: i <= step
                ? `color-mix(in srgb, ${palette.ring} 80%, transparent)`
                : `color-mix(in srgb, ${palette.ring} 22%, transparent)`,
              transition: 'background 0.4s ease',
            }}
          />
        ))}
      </div>
      <DoneBtn onClick={onDone} ring={palette.ring} text={palette.text} />
    </div>
  )
}

// ── Temperature guide ─────────────────────────────────────────────────────

const TEMP_PHRASES = [
  'The air on your skin.',
  'The temperature of your breath.',
  'No need to change anything.',
]

function TemperatureGuide({ onDone, palette }: { onDone: () => void; palette: Palette }) {
  const [phraseIdx, setPhraseIdx] = useState(0)

  useEffect(() => {
    if (phraseIdx >= TEMP_PHRASES.length - 1) return
    const t = setTimeout(() => setPhraseIdx(i => i + 1), 5000)
    return () => clearTimeout(t)
  }, [phraseIdx])

  return (
    <div className={styles.guideView}>
      <PulseCircle ring={palette.ring} bg={palette.bg} />
      <div className={styles.phrases}>
        {TEMP_PHRASES.map((p, i) => (
          <p
            key={i}
            className={styles.phrase}
            style={{
              opacity:   i > phraseIdx ? 0 : i === phraseIdx ? 1 : 0.4,
              transform: i > phraseIdx ? 'translateY(8px)' : 'none',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
              color: palette.text,
            }}
          >
            {p}
          </p>
        ))}
      </div>
      <DoneBtn onClick={onDone} ring={palette.ring} text={palette.text} />
    </div>
  )
}

// ── Sound guide ───────────────────────────────────────────────────────────

const SOUND_DURATION = 45

function SoundGuide({ onDone, palette }: { onDone: () => void; palette: Palette }) {
  const [elapsed, setElapsed] = useState(0)
  const startRef  = useRef<number | null>(null)
  const frameRef  = useRef<number | null>(null)

  useEffect(() => {
    const animate = (ts: number) => {
      if (startRef.current === null) startRef.current = ts
      const e = Math.min((ts - startRef.current) / 1000, SOUND_DURATION)
      setElapsed(e)
      if (e < SOUND_DURATION) frameRef.current = requestAnimationFrame(animate)
    }
    frameRef.current = requestAnimationFrame(animate)
    return () => { if (frameRef.current !== null) cancelAnimationFrame(frameRef.current) }
  }, [])

  return (
    <div className={styles.guideView}>
      <h2 className={styles.guideTitle} style={{ color: palette.text }}>Listen.</h2>
      <CountdownRing elapsed={elapsed} total={SOUND_DURATION} ring={palette.ring} />
      <p className={styles.guideSub} style={{ color: palette.text }}>
        Near sounds. Far sounds. The space between.
      </p>
      <DoneBtn onClick={onDone} ring={palette.ring} text={palette.text} />
    </div>
  )
}

// ── Pause guide ───────────────────────────────────────────────────────────

const PAUSE_DURATION = 60

function PauseGuide({ onDone, palette }: { onDone: () => void; palette: Palette }) {
  const [elapsed, setElapsed] = useState(0)
  const startRef  = useRef<number | null>(null)
  const frameRef  = useRef<number | null>(null)

  const isDone = elapsed >= PAUSE_DURATION

  useEffect(() => {
    const animate = (ts: number) => {
      if (startRef.current === null) startRef.current = ts
      const e = Math.min((ts - startRef.current) / 1000, PAUSE_DURATION)
      setElapsed(e)
      if (e < PAUSE_DURATION) frameRef.current = requestAnimationFrame(animate)
    }
    frameRef.current = requestAnimationFrame(animate)
    return () => { if (frameRef.current !== null) cancelAnimationFrame(frameRef.current) }
  }, [])

  const remaining = Math.ceil(Math.max(0, PAUSE_DURATION - elapsed))

  return (
    <div className={styles.guideView}>
      <p className={styles.pauseLabel} style={{ color: palette.text }}>
        Be still.
      </p>
      <div
        className={styles.pauseNumber}
        style={{ color: palette.ring, opacity: isDone ? 0 : 1, transition: 'opacity 0.8s ease' }}
        aria-live="polite"
        aria-label={isDone ? 'Complete' : `${remaining} seconds`}
      >
        {isDone ? '—' : remaining}
      </div>
      {isDone ? (
        <DoneBtn onClick={onDone} ring={palette.ring} text={palette.text} />
      ) : (
        <button
          className={styles.skipLink}
          style={{ color: palette.text }}
          onClick={onDone}
        >
          skip
        </button>
      )}
    </div>
  )
}

// ── Selection screen ──────────────────────────────────────────────────────

function SelectionScreen({
  onSelect,
  palette,
}: {
  onSelect: (id: OptionId) => void
  palette:  Palette
}) {
  return (
    <div className={styles.selection}>
      <header className={styles.header}>
        <h1 className={styles.heading} style={{ color: palette.text }}>
          A moment to ground
        </h1>
        <p className={styles.sub} style={{ color: palette.text }}>
          Choose what works for you today.
        </p>
      </header>

      <div className={styles.cardGrid}>
        {OPTIONS.map(opt => (
          <button
            key={opt.id}
            className={styles.card}
            style={{
              background:  `color-mix(in srgb, ${palette.swatch} 50%, ${palette.bg})`,
              borderColor: `color-mix(in srgb, ${palette.ring} 35%, transparent)`,
              color: palette.text,
            }}
            onClick={() => onSelect(opt.id)}
          >
            <span className={styles.cardLabel}>{opt.label}</span>
            <span className={styles.cardSub}>{opt.subtitle}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function GroundingPage() {
  const navigate       = useNavigate()
  const [searchParams] = useSearchParams()
  const returnTo       = searchParams.get('return') ?? '/before-we-begin'
  const { palette }    = useTheme()
  const [selected, setSelected] = useState<OptionId | null>(null)

  const handleDone = () => navigate(returnAfterPractice(returnTo))

  const pageStyle: React.CSSProperties = {
    background: palette.bg,
    color:      palette.text,
    fontFamily: "'Lexend', system-ui, sans-serif",
    transition: 'background 0.3s ease',
  }

  const renderGuide = () => {
    switch (selected) {
      case 'feet':        return <FeetGuide        onDone={handleDone} palette={palette} />
      case 'scan':        return <ScanGuide         onDone={handleDone} palette={palette} />
      case 'temperature': return <TemperatureGuide  onDone={handleDone} palette={palette} />
      case 'sound':       return <SoundGuide        onDone={handleDone} palette={palette} />
      case 'pause':       return <PauseGuide        onDone={handleDone} palette={palette} />
      default:            return null
    }
  }

  return (
    <div className={styles.page} style={pageStyle}>
      {selected ? (
        <div className={styles.guideWrap}>
          <button
            className={styles.backLink}
            style={{ color: palette.text }}
            onClick={() => setSelected(null)}
          >
            ← back
          </button>
          {renderGuide()}
        </div>
      ) : (
        <SelectionScreen onSelect={setSelected} palette={palette} />
      )}
    </div>
  )
}
